import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, GraduationCap } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SkillTests() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const load = () => api.get("/skill-tests").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const start = async (skill) => {
    const r = await api.get(`/skill-tests/${skill}`);
    setActive(r.data); setAnswers(new Array(r.data.questions.length).fill(-1)); setResult(null);
  };
  const submit = async () => {
    if (answers.includes(-1)) return toast.error("Answer every question");
    const r = await api.post(`/skill-tests/${active.skill}/submit`, { answers });
    setResult(r.data);
    if (r.data.passed) toast.success(`Passed! Skill verified.`);
    else toast.error("Not passed — try again.");
    load();
  };

  if (active) {
    return (
      <div className="max-w-3xl space-y-5">
        <div><div className="label-caps">Quiz</div><h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">{active.title}</h1></div>
        <div className="text-sm text-slate-600">Pass {active.pass_score}/{active.questions.length} to verify this skill.</div>
        <div className="space-y-4">
          {active.questions.map((q, i) => (
            <div key={q.q} className="card-tactile p-5" data-testid={`quiz-q-${i}`}>
              <div className="font-semibold text-slate-900 mb-3">{i+1}. {q.q}</div>
              <div className="space-y-2">
                {q.options.map((o, j) => (
                  <label key={o} className={`block p-3 rounded-md border cursor-pointer transition-colors ${answers[i] === j ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <input type="radio" className="mr-2" checked={answers[i] === j} onChange={() => { const a = [...answers]; a[i] = j; setAnswers(a); }} data-testid={`quiz-q-${i}-opt-${j}`} />
                    {o}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        {result && (
          <div className={`card-tactile p-5 ${result.passed ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
            <div className="font-display text-xl font-bold text-slate-900">Score: {result.score} / {result.total}</div>
            <div className="text-sm text-slate-700 mt-1">{result.passed ? "You passed! This skill is now verified on your profile." : `You need at least ${result.pass_score} correct.`}</div>
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>{ setActive(null); setResult(null); }}>Back</Button>
          {!result && <Button data-testid="quiz-submit-btn" onClick={submit} className="bg-blue-600 hover:bg-blue-700">Submit</Button>}
          {result && !result.passed && <Button onClick={()=>{ setAnswers(new Array(active.questions.length).fill(-1)); setResult(null); }}>Retake</Button>}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center gap-3">
        <GraduationCap size={28} className="text-blue-600" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Skill tests</h1>
      </div>
      <p className="text-slate-600">Pass a quick quiz to verify a skill. Companies see a green tick next to verified skills.</p>
      <div className="space-y-2">
        {list.map((t) => (
          <div key={t.skill} className="card-tactile p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={`skill-${t.skill}`}>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-display text-xl font-bold text-slate-900">{t.title}</div>
                {t.verified && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md"><CheckCircle size={12} weight="fill"/> Verified</span>}
              </div>
              <div className="text-sm text-slate-600">{t.questions} questions · pass {t.pass_score}</div>
            </div>
            <Button data-testid={`start-${t.skill}`} onClick={()=>start(t.skill)} className={t.verified ? "" : "bg-blue-600 hover:bg-blue-700"} variant={t.verified ? "outline" : "default"}>
              {t.verified ? "Retake" : "Start test"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
